/**
 * workflow-engine/types — shared types for WorkflowEngine + rule files.
 *
 * Ported from SPFx `services/WorkflowEngine.ts` (ComplianceGate, DocumentRequirement)
 * and `services/ProceduralGates.ts` (ValidationResult/ValidationError/ValidationWarning),
 * reshaped around v3's `GateResult` / `GateReport` surface.
 *
 * NO REACT IMPORTS.
 */

import type { CustomerType } from '@retail-ops/shared-types/enums';

// ---------------------------------------------------------------------------
// Gate result / report
// ---------------------------------------------------------------------------

export type GateStatus = 'pass' | 'fail' | 'warn' | 'blocked' | 'pending';

export type GateCategory =
  | 'structure'
  | 'requiredField'
  | 'entityRestriction'
  | 'authorization'
  | 'relationship'
  | 'documentCollection'
  | 'compliance';

export interface RemediationAction {
  label: string;
  /** Deep-link target: a RAO step id (people/products/ownership/compliance/review/sign). */
  deepLinkStepId?: string;
  /** Optional href for non-RAO remediations. */
  href?: string;
  /** Optional field id to scroll-focus on deep-link. */
  fieldId?: string;
}

export interface GateResult {
  gateCode: string;
  entityType: string;
  category: GateCategory;
  status: GateStatus;
  /** Human label for UI — falls back to gateCode when no GATE_LABELS entry. */
  label: string;
  /** Operator-facing explanation shown in GateReport row. */
  reason?: string;
  /** Action shown as a CTA in GateReport. */
  remediationAction?: RemediationAction;
}

export interface GateReport {
  entityType: string;
  family: string;
  gates: GateResult[];
  canCommit: boolean;
  blockerCount: number;
  warnCount: number;
  passCount: number;
}

// ---------------------------------------------------------------------------
// Cart snapshot — normalized view passed into the engine.
//
// SPFx engine consumed `Cart` (with `ownership[]`, `products[]` directly in
// memory).  v3 CartDto keeps those as JSON strings, so the hook parses them
// into this shape and passes it to the engine.
// ---------------------------------------------------------------------------

export interface PersonSnapshot {
  id: string;
  role: string; // 'primary' | 'joint' | 'beneficiary' | 'signer' | 'trustee' | 'custodian' | …
  firstName?: string;
  middleName?: string;
  lastName?: string;
  fullName?: string;
  name2?: string;
  dob?: string;
  taxId?: string;
  tinCode?: string;
  /** No-TIN proof key (name+DOB searched, no match) — see requiredIndividualFields.noTinWaivesTaxId. */
  noTinClearedKey?: string;
  /** Core Director CIF id — present = EXISTING customer. Gates use this to
   *  mirror the server's existing-customer exemptions (Bill 2026-07-23):
   *  completeness + modal-OFAC requirements don't apply; the server rescreen
   *  backstop covers OFAC silently at commit-validate (alpha.354). */
  cifId?: string;
  customerType?: CustomerType | string;
  addr?: string;
  city?: string;
  state?: string;
  zip?: string;
  /** CIP fields (254–258) — from `extra` in the RAO types. */
  idTypeCode?: string;
  idNumber?: string;
  idIssuer?: string;
  idIssueDate?: string;
  idExpiryDate?: string;
  /** OFAC status — written by the Person Modal (OfacScanSection) to
   *  person.extra.ofacStatus; normalize reads via shared/ofacStatus.
   *  (ComplianceStep, the previous writer, was retired 2026-05-08 and its
   *  zombie mount deleted in BACKLOG-0746.) */
  ofacStatus?: 'idle' | 'running' | 'clear' | 'hit' | 'error';
  /** CIP status rollup. */
  cipStatus?: 'clear' | 'pending' | 'exception';
  cddExempt?: boolean;
  cddExemptReasonCode?: string;
  citizenshipStatus?: string;
  countryOfCitizenship?: string;
  /** Fiduciary / minor linkage — see SPFx Person custodianCifId / protectedPersonCifId. */
  custodianCifId?: string;
  conservateeCifId?: string;
  protectedPersonCifId?: string;
  /** Document on-file flags from SPFx Person. */
  probateDocOnFile?: boolean;
  courtOrderOnFile?: boolean;
  /** Catch-all for Wave 3 fields still on RaoPerson.extra. */
  extra?: Record<string, unknown>;
}

export interface ProductSnapshot {
  code: string;
  acctType?: string;
  qty?: number;
  openingDeposit?: number;
  featureSelections?: Record<string, unknown>;
  odpEnabled?: boolean;
}

export interface DocumentSnapshot {
  docType: string;
  required: boolean;
  collected: boolean;
  source?: 'entity' | 'product' | 'feature';
}

export interface CartSnapshot {
  cartId: string;
  entityType?: CustomerType | string;
  /** True when the cart ownership is Joint (applies individual + joint gates). */
  isJoint: boolean;
  people: PersonSnapshot[];
  products: ProductSnapshot[];
  documents: DocumentSnapshot[];
  /** Configurable-compliance-question answers keyed by questionId. */
  complianceAnswers?: Record<string, boolean>;
  /** BO matrix (business entities) — SPFx `BeneficialOwner[]`. */
  beneficialOwners?: Array<{ name: string; percent: number; isController: boolean }>;
  /**
   * Pre-AcctAdd Risk Mgmt review decision (Irrevocable Trust / SNT / Estate
   * court order / Conservatorship etc.). Mirrors CartDto.riskReviewStatus and
   * is read by RISK_MGMT_REVIEW + COURT_ORDER_REVIEW gates. `undefined` means
   * the pipeline hasn't kicked off risk review yet for this cart.
   */
  riskReviewStatus?: 'NotRequired' | 'Pending' | 'Approved' | 'Rejected' | 'Returned';
  /** Raw metadata for rules that need to peek into future fields. */
  metadata?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Gate labels (ported from SPFx GATE_LABELS + added v3 codes)
// ---------------------------------------------------------------------------

export const GATE_LABELS: Record<string, string> = {
  // Structure
  CART_NO_PEOPLE: 'At least one applicant required',
  CART_NO_PRODUCTS: 'At least one product required',

  // Core compliance (ported from SPFx WorkflowEngine.GATE_LABELS)
  CIP: 'Customer Identification (CIP)',
  CDD: 'Customer Due Diligence (CDD)',
  OFAC: 'OFAC Screening',
  BO: 'Beneficial Ownership (25%+ owners + control person)',
  BO_CONTROLLER_ONLY: 'Beneficial Ownership (control person only)',
  SOS_VERIFY: 'Secretary of State Verification',
  COURT_ORDER_REVIEW: 'Court Order Review',
  RISK_MGMT_REVIEW: 'Risk Management Review',
  VISA_VALIDATION: 'Visa Type Validation (reject B-visa/VWP)',

  // Required-field gates (MISSING_* — SPFx _checkFields())
  MISSING_FIRSTNAME: 'First name required',
  MISSING_LASTNAME: 'Last name required',
  MISSING_TAXID: 'Tax ID (SSN/EIN) required',
  MISSING_DOB: 'Date of birth required',
  MISSING_ADDR: 'Street address required',
  MISSING_CITY: 'City required',
  MISSING_STATE: 'State required',
  MISSING_ZIP: 'ZIP code required',
  MISSING_IDTYPECODE: 'ID type required',
  MISSING_IDNUMBER: 'ID number required',
  NONCIT_COUNTRY: 'Country of citizenship required',

  // Entity restrictions (SPFx _validateEntityRestrictions())
  ESTATE_NO_ODP: 'Estate cannot have ODP',
  ESTATE_NO_DEBIT: 'Estate cannot have debit card',
  REPPAYEE_NO_DEBIT: 'Rep Payee cannot have debit card',
  MINOR_NO_ODP: 'Minor cannot have ODP',
  CONSERV_NO_ODP: 'Conservator cannot have ODP',
  CONSERV_NO_DEBIT: 'Conservator cannot have debit card',
  UTMA_NO_ODP: 'UTMA cannot have ODP',

  // Authorization
  AUTH_FINANCE_REQUIRED: 'Finance department approval required',

  // Relationship (SPFx _validateRelationships())
  JOINT_MIN_OWNERS: 'Joint accounts require 2+ owners',
  MINOR_GUARDIAN: 'Minor requires a guardian linked',
  UTMA_CUSTODIAN: 'UTMA requires a custodian linked',
  CONSERV_PROTECTED: 'Protected person CIF must be linked',
  REPPAYEE_BENEFICIARY: 'Beneficiary CIF must be linked',
  TRUST_TRUSTEE: 'Trust requires 1+ trustee signer',
  ESTATE_PERSONAL_REP: 'Estate requires personal representative',
  PARTNER_MIN_SIGNERS: 'Partnership requires 2+ authorized signers',
  BIZ_AUTH_SIGNER: 'Business requires 1+ authorized signer',
  DBA_BUSINESS_LINK: 'DBA name required',

  // Document collection (SPFx _validateDocumentCollection)
  ESTATE_COURT_DOCS: 'Estate court documents on file',
  CONSERV_COURT_DOCS: 'Conservator court order on file',
  REPPAYEE_SSA_LETTER: 'SSA appointment letter collected',
  PUBFUNDS_FINANCE_AUTH: 'Public funds — Finance authorization collected',

  // v3 additions (not in SPFx — gates fire inline at submit/preflight/post-sign;
  // the retired ComplianceStep surface no longer exists, BACKLOG-0746)
  COMPLIANCE_QUESTIONS_ANSWERED: 'Required compliance questions answered',
  USA_PATRIOT_DELIVERED: 'USA PATRIOT disclosure delivered',
  SIG_CARD_GENERATED: 'Signature card generated',
  ESCHEATMENT_JURISDICTION: 'Escheatment jurisdiction confirmed',
  W9_COLLECTED: 'W-9 collected',
  W8_COLLECTED: 'W-8BEN collected',
};
